import React, { useEffect, useRef, useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import config from 'src/config';

interface GoogleMapsComponentProps {
	markerPosition?: { lat: number; lng: number };
	onMarkerDrag?: (lat: number, lng: number) => void;
	onPlaceSelect?: (placeId: string, address: any) => void;
	circleRadius?: number;
	mapKey?: string;
	zoom?: number;
}

declare global {
	interface Window {
		google: any;
		initGoogleMaps: () => void;
	}
}

// Configuration constants
const GOOGLE_MAPS_CONFIG = {
	API_KEY: config.google.mapsApiKey,
	LIBRARIES: 'places,marker',
	MAP_ID: 'DEMO_MAP_ID',
	LOADING_TIMEOUT: 10000,
};

const MAP_DEFAULTS = {
	CENTER: { lat: 40.7128, lng: -74.006 }, // New York City
	ZOOM: 16,
	BOUNDS_PADDING: 50,
};

const CIRCLE_STYLE = {
	strokeColor: '#2C6ECB',
	strokeOpacity: 0.8,
	strokeWeight: 1,
	fillColor: '#2C6ECB',
	fillOpacity: 0.15,
};

const ZOOM_LEVELS = [
	{ maxRadius: 1, zoom: 14 },
	{ maxRadius: 2, zoom: 13 },
	{ maxRadius: 5, zoom: 12 },
	{ maxRadius: 10, zoom: 11 },
	{ maxRadius: 20, zoom: 10 },
	{ maxRadius: 50, zoom: 9 },
	{ maxRadius: 100, zoom: 8 },
	{ maxRadius: Infinity, zoom: 7 },
];

const EARTH_RADIUS_METERS = 6378137;
const MILES_TO_METERS = 1609.34;

const GoogleMapsComponent: React.FC<GoogleMapsComponentProps> = ({
	markerPosition,
	onMarkerDrag,
	onPlaceSelect,
	circleRadius,
	mapKey,
	zoom,
}) => {
	const mapRef = useRef<HTMLDivElement>(null);
	const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
	const [googleMapsError, setGoogleMapsError] = useState<string | null>(null);
	const mapInstanceRef = useRef<any>(null);
	const markerRef = useRef<any>(null);
	const circleRef = useRef<any>(null);
	const isDraggingRef = useRef(false); // Flag to track if marker is being dragged
	const lastMarkerPositionRef = useRef<{ lat: number; lng: number } | null>(
		null,
	); // Track last position to detect new selections
	const lastMapKeyRef = useRef<string | undefined>(mapKey); // Track map key for forced refresh

	// Function to reverse geocode coordinates and get place details
	const reverseGeocode = async (lat: number, lng: number) => {
		if (!window.google || !window.google.maps) {
			console.error('Google Maps API not available for reverse geocoding');
			return;
		}

		try {
			const geocoder = new window.google.maps.Geocoder();
			const latlng = new window.google.maps.LatLng(lat, lng);

			const result = await new Promise<any>((resolve, reject) => {
				geocoder.geocode({ location: latlng }, (results: any, status: any) => {
					if (status === 'OK' && results && results.length > 0) {
						resolve(results[0]);
					} else {
						reject(new Error(`Geocoding failed: ${status}`));
					}
				});
			});

		const placeId = result.place_id;
		const addressComponents = result.address_components || [];

		const getComponent = (type: string) => {
			const component = addressComponents.find((comp: any) =>
				comp.types.includes(type),
			);
			return component ? component.long_name : '';
		};

		const getComponentShortName = (type: string) => {
			const component = addressComponents.find((comp: any) =>
				comp.types.includes(type),
			);
			return component ? component.short_name : '';
		};

		const address = {
			street: getComponent('street_number') + ' ' + getComponent('route'),
			city: getComponent('locality') || getComponent('sublocality'),
			state: getComponent('administrative_area_level_1'),
			postalCode: getComponent('postal_code'),
			country: getComponent('country'),
			lat: lat.toString(),
			long: lng.toString(),
			formatted: result.formatted_address || '',
			placeId: placeId,
			regionCode: getComponentShortName('country'),
		};

		return { placeId, address };
		} catch (error) {
			console.error('Error in reverse geocoding:', error);
			return null;
		}
	};

	// Load Google Maps API
	useEffect(() => {
		const loadGoogleMaps = () => {
			// Check if API key is configured
			if (!GOOGLE_MAPS_CONFIG.API_KEY) {
				setGoogleMapsError('Google Maps API key not configured');
				console.error(
					'Google Maps API key not found. Please set VITE_GOOGLE_MAPS_API_KEY in your environment variables.',
				);
				return;
			}

			// Check if already loaded - verify all necessary APIs are available
			if (window.google && window.google.maps && window.google.maps.Map) {
				setIsGoogleMapsLoaded(true);
				return;
			}

			// Check if script is already loading
			const existingScript = document.querySelector(
				'script[src*="maps.googleapis.com"]',
			);
			if (existingScript) {
				// Script is loading, wait for it to be fully ready
				let retryCount = 0;
				const maxRetries = 50; // 5 seconds max (50 * 100ms)
				const checkLoaded = () => {
					if (window.google && window.google.maps && window.google.maps.Map) {
						setIsGoogleMapsLoaded(true);
					} else if (retryCount < maxRetries) {
						retryCount++;
						setTimeout(checkLoaded, 100);
					} else {
						setGoogleMapsError('Google Maps API loading timeout');
					}
				};
				checkLoaded();
				return;
			}

			// Set up callback - wait for full API to be ready
			window.initGoogleMaps = () => {
				// Double-check that all necessary APIs are loaded
				let retryCount = 0;
				const maxRetries = 50; // 5 seconds max (50 * 100ms)
				const checkApiReady = () => {
					if (window.google && window.google.maps && window.google.maps.Map) {
						setIsGoogleMapsLoaded(true);
					} else if (retryCount < maxRetries) {
						retryCount++;
						setTimeout(checkApiReady, 100);
					} else {
						setGoogleMapsError('Google Maps API not fully loaded after callback');
					}
				};
				checkApiReady();
			};

			// Create and load script with marker library for AdvancedMarkerElement
			const script = document.createElement('script');
			script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_CONFIG.API_KEY}&libraries=${GOOGLE_MAPS_CONFIG.LIBRARIES}&loading=async&callback=initGoogleMaps`;
			script.async = true;
			script.defer = true;
			script.onerror = () => {
				setGoogleMapsError('Failed to load Google Maps API');
			};

			// Set timeout for loading
			const timeout = setTimeout(() => {
				if (!window.google || !window.google.maps) {
					setGoogleMapsError('Google Maps API loading timeout');
				}
			}, GOOGLE_MAPS_CONFIG.LOADING_TIMEOUT);

			script.onload = () => {
				clearTimeout(timeout);
			};

			document.head.appendChild(script);

			// Cleanup function
			return () => {
				clearTimeout(timeout);
				(window as any).initGoogleMaps = undefined;
			};
		};

		loadGoogleMaps();
	}, []);

	// Calculate appropriate zoom level based on circle radius
	const getZoomLevelForRadius = (radius: number): number => {
		const level = ZOOM_LEVELS.find((level) => radius <= level.maxRadius);
		return level?.zoom || MAP_DEFAULTS.ZOOM;
	};

	// Helper functions
	const isValidPosition = (position?: { lat: number; lng: number }) => {
		return (
			position &&
			typeof position.lat === 'number' &&
			typeof position.lng === 'number' &&
			!isNaN(position.lat) &&
			!isNaN(position.lng)
		);
	};

	const createCircle = (
		center: { lat: number; lng: number },
		radiusInMiles: number,
	) => {
		return new window.google.maps.Circle({
			...CIRCLE_STYLE,
			map: mapInstanceRef.current,
			center,
			radius: radiusInMiles * MILES_TO_METERS,
		});
	};

	const fitMapToCircle = (
		center: { lat: number; lng: number },
		radiusInMiles: number,
	) => {
		if (!mapInstanceRef.current) return;

		const bounds = new window.google.maps.LatLngBounds();
		const radiusInMeters = radiusInMiles * MILES_TO_METERS;
		const { lat, lng } = center;
		const latRadian = lat * (Math.PI / 180);

		// Calculate coordinate deltas for the radius
		const deltaLat = (radiusInMeters / EARTH_RADIUS_METERS) * (180 / Math.PI);
		const deltaLng =
			(radiusInMeters / (EARTH_RADIUS_METERS * Math.cos(latRadian))) *
			(180 / Math.PI);

		// Extend bounds to circle perimeter points
		const cornerPoints = [
			[lat + deltaLat, lng + deltaLng],
			[lat - deltaLat, lng - deltaLng],
			[lat + deltaLat, lng - deltaLng],
			[lat - deltaLat, lng + deltaLng],
		];

		cornerPoints.forEach(([lat, lng]) => {
			bounds.extend(new window.google.maps.LatLng(lat, lng));
		});

		mapInstanceRef.current.fitBounds(bounds, {
			padding: MAP_DEFAULTS.BOUNDS_PADDING,
		});
	};

	const cleanupMap = () => {
		if (markerRef.current) {
			markerRef.current.setMap(null);
			markerRef.current = null;
		}
		if (circleRef.current) {
			circleRef.current.setMap(null);
			circleRef.current = null;
		}
		mapInstanceRef.current = null;
		lastMarkerPositionRef.current = null;
	};

	// Initialize map
	useEffect(() => {
		if (!isGoogleMapsLoaded || !mapRef.current) return;

		try {
			// Check if Google Maps API is fully loaded
			if (!window.google?.maps?.Map) {
				console.error('Google Maps API not fully loaded');
				setGoogleMapsError('Google Maps API not fully loaded');
				return;
			}

			if (lastMapKeyRef.current !== mapKey && mapInstanceRef.current) {
				cleanupMap();
			}
			lastMapKeyRef.current = mapKey;

			// Use marker position if valid, otherwise use default center
			const center = isValidPosition(markerPosition)
				? markerPosition!
				: MAP_DEFAULTS.CENTER;
			// Use prop zoom if provided, otherwise calculate from circleRadius or use default
			const mapZoom = zoom ?? (circleRadius
				? getZoomLevelForRadius(circleRadius)
				: MAP_DEFAULTS.ZOOM);

			// Create map with AdvancedMarkerElement support
			const map = new window.google.maps.Map(mapRef.current, {
				center,
				zoom: mapZoom,
				mapTypeId: 'roadmap',
				mapId: GOOGLE_MAPS_CONFIG.MAP_ID,
			});

			mapInstanceRef.current = map;

			// Center map on marker position if provided
			if (isValidPosition(markerPosition)) {
				map.setCenter(markerPosition!);
			}

			// Add click listener to map to get coordinates for clicked location
			map.addListener('click', async (event: any) => {
				const clickedLat = event.latLng.lat();
				const clickedLng = event.latLng.lng();

				console.log('Map clicked at coordinates:', {
					lat: clickedLat,
					lng: clickedLng,
				});

				// Call onMarkerDrag with the clicked location
				if (onMarkerDrag) {
					onMarkerDrag(clickedLat, clickedLng);
				}

				// Reverse geocode to get place details
				const placeDetails = await reverseGeocode(clickedLat, clickedLng);
				if (placeDetails && onPlaceSelect) {
					onPlaceSelect(placeDetails.placeId, placeDetails.address);
				}
			});

			// Don't create marker or circle here - that will be handled by the separate useEffect
		} catch (error) {
			console.error('Error initializing map:', error);
			setGoogleMapsError('Failed to initialize map');
		}
	}, [isGoogleMapsLoaded, mapKey]); // Depend on API loading and mapKey for forced refresh

	useEffect(() => {
		if (!isGoogleMapsLoaded || !mapInstanceRef.current) return;
		
		if (zoom !== undefined) {
			mapInstanceRef.current.setZoom(zoom);
		}
	}, [zoom, isGoogleMapsLoaded]);

	// Update marker position when prop changes
	useEffect(() => {
		if (!isGoogleMapsLoaded || !mapInstanceRef.current) return;

		// Skip updating if marker is currently being dragged
		if (isDraggingRef.current) return;

		try {
			if (isValidPosition(markerPosition)) {
				const position = new window.google.maps.LatLng(
					markerPosition!.lat,
					markerPosition!.lng,
				);

				// Update or create marker
				if (markerRef.current) {
					// Update existing marker position
					if (markerRef.current.position) {
						// AdvancedMarkerElement
						markerRef.current.position = markerPosition;
					} else {
						// Legacy Marker
						markerRef.current.setPosition(position);
					}
				} else {
					// Create new marker
					try {
						if (
							window.google.maps.marker &&
							window.google.maps.marker.AdvancedMarkerElement
						) {
							const marker =
								new window.google.maps.marker.AdvancedMarkerElement({
									position: markerPosition,
									map: mapInstanceRef.current,
									gmpDraggable: true,
								});

							markerRef.current = marker;

							// Add drag event listener for AdvancedMarkerElement
							marker.addListener('dragstart', () => {
								isDraggingRef.current = true;
							});
							marker.addListener('dragend', () => {
								isDraggingRef.current = false;
								if (marker.position && onMarkerDrag) {
									onMarkerDrag(marker.position.lat, marker.position.lng);
								}
							});
						} else {
							// Fallback to legacy Marker
							const marker = new window.google.maps.Marker({
								position,
								map: mapInstanceRef.current,
								draggable: true,
							});

							markerRef.current = marker;

							// Add drag event listener for legacy Marker
							marker.addListener('dragstart', () => {
								isDraggingRef.current = true;
							});
							marker.addListener('dragend', () => {
								isDraggingRef.current = false;
								const newPosition = marker.getPosition();
								if (newPosition && onMarkerDrag) {
									onMarkerDrag(newPosition.lat(), newPosition.lng());
								}
							});
						}
					} catch (error) {
						console.error('Error creating marker:', error);
					}
				}

				// Update circle center if it exists (circle creation is handled in separate useEffect)
				if (circleRef.current) {
					circleRef.current.setCenter(position);
				}

				// Don't center map here - centering is handled in separate useEffect
			} else {
				// Remove marker if no position or invalid position
				if (markerRef.current) {
					markerRef.current.setMap(null);
					markerRef.current = null;
				}
				// Clear the last position reference
				lastMarkerPositionRef.current = null;
				// Note: Circle removal is handled by the circle useEffect
			}
		} catch (error) {
			console.error('Error updating marker:', error);
		}
	}, [markerPosition, isGoogleMapsLoaded, mapKey]); // Depend on marker position, API loading, and mapKey for refresh

	// Handle centering when new address is selected (not when dragging)
	useEffect(() => {
		if (
			!isGoogleMapsLoaded ||
			!mapInstanceRef.current ||
			!isValidPosition(markerPosition)
		) {
			return;
		}

		if (isDraggingRef.current) return;

		const lastPos = lastMarkerPositionRef.current;
		const positionChanged = !lastPos || 
			lastPos.lat !== markerPosition!.lat || 
			lastPos.lng !== markerPosition!.lng;

		if (positionChanged) {
			mapInstanceRef.current.setCenter(markerPosition!);
			lastMarkerPositionRef.current = markerPosition!;
			
			if (zoom !== undefined) {
				mapInstanceRef.current.setZoom(zoom);
			}
		}
	}, [markerPosition, isGoogleMapsLoaded, zoom, mapKey]);

	// Update circle radius when prop changes
	useEffect(() => {
		if (!isGoogleMapsLoaded || !mapInstanceRef.current) return;

		try {
			// Remove existing circle first
			if (circleRef.current) {
				circleRef.current.setMap(null);
				circleRef.current = null;
			}

			// Create circle if we have both radius and valid position
			if (circleRadius && isValidPosition(markerPosition)) {
				const circle = createCircle(markerPosition!, circleRadius);
				circleRef.current = circle;
				fitMapToCircle(markerPosition!, circleRadius);
			}
		} catch (error) {
			console.error('Error updating circle:', error);
		}
	}, [circleRadius, markerPosition, isGoogleMapsLoaded, mapKey]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			cleanupMap();
		};
	}, []);

	if (googleMapsError) {
		return (
			<Box
				width="100%"
				height="400px"
				bg="gray.100"
				display="flex"
				alignItems="center"
				justifyContent="center"
				borderRadius="md"
			>
				<Text color="red.500">Map unavailable: {googleMapsError}</Text>
			</Box>
		);
	}

	if (!isGoogleMapsLoaded) {
		return (
			<Box
				width="100%"
				height="400px"
				bg="gray.100"
				display="flex"
				alignItems="center"
				justifyContent="center"
				borderRadius="md"
			>
				<Text>Loading map...</Text>
			</Box>
		);
	}

	return (
		<Box
			ref={mapRef}
			width="100%"
			height="400px"
			borderRadius="md"
			overflow="hidden"
		/>
	);
};

export default GoogleMapsComponent;
